/**
 * /academy/attempt/:id and its results: the sitting says its exam (`exam` in the state), so the
 * IELTS player and results open for an IELTS sitting and the TCF screens stay as they are.
 */

import { useAcademyAttemptsRetrieve } from "@agencyos/api-client";
import { Skeleton } from "@agencyos/ui-web";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import { PlayerPage } from "../PlayerPage";
import { ResultsPage } from "../ResultsPage";
import { useAttemptInfo } from "../shared";
import { isObject } from "../types";
import { GoethePlayer } from "../goethe/GoethePlayer";
import { GoetheResults } from "../goethe/GoetheResults";
import { TefResults } from "../tef/TefResults";
import { TrainerReviewPanel } from "../TrainerReview";
import { IELTS, TEF } from "./exam";
import { IeltsPlayer } from "./IeltsPlayer";
import { IeltsResults } from "./IeltsResults";

function useAttemptExam(id: string, retry: number | false): { exam: string; loading: boolean } {
  const query = useAcademyAttemptsRetrieve(id, { query: { retry, staleTime: 0 } });
  const data: unknown = query.data?.status === 200 ? query.data.data : undefined;
  return { exam: isObject(data) && typeof data.exam === "string" ? data.exam : "", loading: query.isLoading };
}

/** D-174: « Refaire » found this activity already open: it is resumed, and the screen says so. */
function ResumedNote() {
  const { t } = useTranslation();
  const { state } = useLocation();
  if (!isObject(state) || state.resumed !== true) return null;
  return (
    <p className="ac-note" role="status">
      {t("academyHistory.retake.resumedNote")}
    </p>
  );
}

export function PlayerSwitch() {
  return (
    <>
      <ResumedNote />
      <PlayerByExam />
    </>
  );
}

function PlayerByExam() {
  const { id = "" } = useParams();
  const { exam, loading } = useAttemptExam(id, 1);
  const { kind, mode } = useAttemptInfo(id, exam);
  if (loading) {
    return (
      <div className="ac-player ac-player--loading">
        <Skeleton style={{ height: 64 }} />
        <Skeleton style={{ height: 360 }} />
      </div>
    );
  }
  if (exam === TEF) return <IeltsPlayer id={id} kind={kind} mode={mode} exam={TEF} />;
  // German (D-118): a placement runs on the course exercises, like the IELTS one
  if (exam === "goethe") return kind === "placement" ? <IeltsPlayer id={id} kind={kind} mode={mode} exam="goethe" /> : <GoethePlayer id={id} />;
  return exam === IELTS ? <IeltsPlayer id={id} kind={kind} mode={mode} /> : <PlayerPage />;
}

export function ResultsSwitch() {
  const { id = "" } = useParams();
  const { exam, loading } = useAttemptExam(id, false);
  if (loading) return <Skeleton style={{ height: 320 }} />;
  const results =
    exam === TEF ? <TefResults id={id} /> : exam === "goethe" ? <GoetheResults id={id} /> : exam === IELTS ? <IeltsResults id={id} /> : <ResultsPage />;
  // D-124: « demander la revue d'un formateur », for every exam
  return (
    <>
      {results}
      <TrainerReviewPanel attemptId={id} />
    </>
  );
}
